import { type JSX, createContext } from 'react';

/**
 * List of available user roles. If a new role is added to this, it likely needs to be added to Landing.tsx, which determines the homepage for each role.
 */
export enum Role {
    ADMIN = 'admin',
    CONTROL_USER = 'control user',
    CONTROL_USER_READ_ONLY = 'control user (read-only)',
    REPORTING_USER = 'reporting user',
    RISK_MANAGER = 'risk manager',
    RISK_MANAGER_READ_ONLY = 'risk manager (read-only)',
    RISK_USER = 'risk user',
    RISK_USER_READ_ONLY = 'risk user (read-only)',
    THIRD_PARTY_USER = 'third party user',
    THIRD_PARTY_USER_READ_ONLY = 'third party user (read-only)',
    UCRM_USER = 'ai governance user',
    UCRM_USER_READ_ONLY = 'ai governance user (read-only)',
}

/**
 * Compare a list of roles that a user has with another list of roles. Used to determine if a user should have access to something.
 * If simply wrapping a component for conditional rendering, use <RBACComponent>.
 * @param userRoles list of roles the user has.
 * @param authorizedRoles list of roles to compare the user's roles with.
 * @returns true if a user is an admin or has at least one role in authorizedRoles, else false.
 */
export const userHasAuthorizedRole = (userRoles: Role[], authorizedRoles: Role[]): boolean => {
    return userRoles.includes(Role.ADMIN) || authorizedRoles.filter((role) => userRoles.includes(role)).length > 0;
};

export interface ContextProps {
    roles: Role[];
}

export const RBACContext = createContext<ContextProps | undefined>(undefined);

interface UserProviderProps {
    children: React.ReactNode;
    roles: Role[];
}

export const RBACProvider = (props: UserProviderProps): JSX.Element => {
    return <RBACContext.Provider value={{ roles: props.roles }}>{props.children}</RBACContext.Provider>;
};
